import { createListenerMiddleware } from '@reduxjs/toolkit'
import * as Analytics from 'analytics/analytics'
import { trackPageView } from 'analytics/snowplow/events'
import { waitPageViewDataLoaded, REDUX_STATE_OBSERVER_TIMEOUT, getCurrentPathFromDocument, checkIfShouldWaitForAnalyticsData } from '../analytics/snowplow/helpers'
import { PAGE_VIEW } from 'actions/actionConstants'
import { waitForValue } from 'lib/promise/waitForValue'
import { AnalyticsDataAvailableEvent } from 'hooks/Analytics/usePageViewAnalyticsPriceShare'

const analyticsPageViewMiddleware = createListenerMiddleware()

const pageChangedBetweenWait = (oldState:App.State, newState:App.State) => {
  return oldState.routeHistory.currentPath !== newState.routeHistory.currentPath
}

const waitForAnalyticsDataAvailable = waitForValue<AnalyticsDataAvailableEvent>()

document.addEventListener('analytics-data-available', (event: CustomEvent<AnalyticsDataAvailableEvent>) => {
  waitForAnalyticsDataAvailable.resolveWithValue(event.detail)
})

analyticsPageViewMiddleware.startListening({
  type: PAGE_VIEW,
  effect: async(action, listenerApi) => {
    const state = listenerApi.getState() as App.State
    if (state.routeHistory.currentPath === state.routeHistory.prevPath) return
    const shouldWaitForAnalyticsData = checkIfShouldWaitForAnalyticsData(state)

    let analyticsDataTimeoutId: ReturnType<typeof setTimeout> | undefined

    const analyticsDataPromise = shouldWaitForAnalyticsData ?
      Promise.race([
        waitForAnalyticsDataAvailable.awaitForValue(),
        new Promise<undefined>((resolve) => {
          analyticsDataTimeoutId = setTimeout(() => {
            resolve(undefined)
          }, REDUX_STATE_OBSERVER_TIMEOUT)
        }),
      ]).finally(() => {
        if (analyticsDataTimeoutId) clearTimeout(analyticsDataTimeoutId)
      }) : Promise.resolve(undefined)

    // check conditions for awaiting specific redux state data items
    const [numberOfItemsWaitedFor, analyticsDataAvailableEvent] = await Promise.all([
      waitPageViewDataLoaded(state, listenerApi),
      analyticsDataPromise,
    ])

    if (shouldWaitForAnalyticsData) {
      waitForAnalyticsDataAvailable.resetAwaitedValue()
    }

    const newState = listenerApi.getState() as App.State
    // if page changed between wait abandon tracking, the new page view will also trigger and with the correct data
    if (numberOfItemsWaitedFor.length > 0 && pageChangedBetweenWait(state, newState)) return
    if (document.location.pathname !== state.routeHistory.currentPath) return

    // If analyticsDataAvailableEvent is undefined, it's unfortunate but proceed with tracking anyway. Otherwise, only proceed if the storage key includes current path
    if (analyticsDataAvailableEvent && !analyticsDataAvailableEvent.storageKey.includes(getCurrentPathFromDocument())) return

    Analytics.trackEvent(trackPageView(state.routeHistory.currentPath, state.routeHistory.currentPathParams))
  },
})

export default analyticsPageViewMiddleware
